<template>
  <div class="games-filter-mobile">
    <client-only>
      <div
        class="games-filter-mobile-activator"
        :class="{ 'games-filter-mobile-activator--active': isActive }"
        @click="toggleFilter('open')"
      >
        <atomic-icon id="provider-filter" />

        <div v-if="isActive" class="games-filter-mobile-activator__circle" />
      </div>

      <layout-bottom-sheet ref="bottomSheet" height="63" @closed="parseQuery">
        <template #header>
          <div class="games-filter-mobile__header">
            <div class="games-filter-mobile__title">
              {{ getContent(gamesContent?.currentLocaleData, gamesContent?.defaultLocaleData, 'filter_title') }}
            </div>

            <button-text v-if="showResetBtn" @click="onResetAll">
              {{ getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.resetAll') }}
            </button-text>
          </div>
        </template>

        <div class="games-filter-mobile__body">
          <div class="games-filter-mobile__body-item">
            <div class="games-filter-mobile__item-label">
              {{ getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.feature') }}
            </div>

            <selects-wrappers-responsive-select
              v-model:selectedOptions="selectedFeatures"
              :options="featuresOptions"
              :title="getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.feature')"
              :label="getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.allFeatures')"
              :showResetBtn="showResetBtnFeatures"
              multiple
              @onResetOptions="onResetFeatures"
            >
              <template #bottom-sheet-footer="{ onClose }">
                <button-base type="gray" size="md" @click="onClose">
                  {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.back') }}
                </button-base>

                <button-base type="primary" size="md" @click="onClose">
                  {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.save') }}
                </button-base>
              </template>
            </selects-wrappers-responsive-select>
          </div>

          <div v-if="!hideProviders" class="games-filter-mobile__body-item">
            <div class="games-filter-mobile__item-label">
              {{ getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.provider') }}
            </div>

            <selects-wrappers-responsive-select
              v-model:selectedOptions="selectedProviders"
              :options="providersOptions"
              :title="getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.provider')"
              :label="getContent(popupsData, defaultLocalePopupsData, 'gamesFilter.allProviders')"
              :showResetBtn="showResetBtnProviders"
              multiple
              @onResetOptions="onResetProviders"
            >
              <template #bottom-sheet-footer="{ onClose }">
                <button-base type="gray" size="md" @click="onClose">
                  {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.back') }}
                </button-base>

                <button-base type="primary" size="md" @click="onClose">
                  {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.save') }}
                </button-base>
              </template>
            </selects-wrappers-responsive-select>
          </div>

          <div class="games-filter-mobile__body-item">
            <div class="games-filter-mobile__item-label">
              {{ getContent(gamesContent?.currentLocaleData, gamesContent?.defaultLocaleData, 'sortLabel') }}
            </div>

            <selects-wrappers-responsive-select
              :selectedOption="activeOption.value"
              :title="getContent(gamesContent?.currentLocaleData, gamesContent?.defaultLocaleData, 'sortLabel')"
              :label="getContent(gamesContent?.currentLocaleData, gamesContent?.defaultLocaleData, 'sortLabel')"
              :options="sortOptions"
              identifierKey="value"
              bottomSheetHeight="63"
              hideActionBtn
              @update:selectedOption="onSelectOption"
            />
          </div>
        </div>

        <template #footer>
          <div class="games-filter-mobile__buttons">
            <button-base class="games-filter-mobile__button" type="gray" size="md" @click="onClose">
              {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.cancel') }}
            </button-base>

            <button-base
              class="games-filter-mobile__button"
              type="primary"
              size="md"
              :isDisabled="isApplyButtonDisabled"
              @click="onApplyFilter"
            >
              {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.apply') }}
            </button-base>
          </div>
        </template>
      </layout-bottom-sheet>
    </client-only>
  </div>
</template>

<script setup lang="ts">
  import isEqual from 'lodash/isEqual';

  import { normalizeToArray } from '@skeleton/utils/arrayUtils';

  import type { ISortOption } from '@skeleton/core/types';

  const props = withDefaults(
    defineProps<{
      isActive: boolean;
      hideProviders?: boolean;
      featuresOptions: any[];
      providersOptions: any[];
      sortBy: string | undefined;
      sortOrder: string | undefined;
      sortOptions: ISortOption[];
    }>(),
    {
      isActive: false,
      hideProviders: false,
    }
  );

  const { getContent } = useProjectMethods();
  const { popupsData, defaultLocalePopupsData, layoutData, defaultLocaleLayoutData } = storeToRefs(useGlobalStore());
  const { content: gamesContent } = useGamesContent();

  const { selectedOption } = await useSortingOptions();

  const route = useRoute();
  const router = useRouter();

  const bottomSheet = ref();

  const selectedProviders = ref<string[]>([]);
  const selectedFeatures = ref<string[]>([]);
  const activeOption = ref<Record<string, any>>({});

  const isFeaturesResetVisible = computed(() => {
    const { features } = route.query;
    return !isEqual(normalizeToArray(features), selectedFeatures.value);
  });

  const isProvidersResetVisible = computed(() => {
    const { providers } = route.query;
    return !isEqual(normalizeToArray(providers), selectedProviders.value);
  });

  const isSortingResetVisible = computed(() => {
    const { sortOrder, sortBy } = route.query;
    return activeOption.value.sortOrder !== sortOrder || activeOption.value.sortBy !== sortBy;
  });

  const isApplyButtonDisabled = computed(() => {
    return !(isFeaturesResetVisible.value || isProvidersResetVisible.value || isSortingResetVisible.value);
  });

  const showResetBtnFeatures = computed(() => !!selectedFeatures.value.length);

  const showResetBtnProviders = computed(() => !!selectedProviders.value.length);

  const showResetBtn = computed(() => {
    if (props.hideProviders) {
      return showResetBtnFeatures.value || isSortingResetVisible.value;
    }

    return showResetBtnFeatures.value || showResetBtnProviders.value || isSortingResetVisible.value;
  });

  const onResetFeatures = () => {
    selectedFeatures.value = [];
  };

  const onResetProviders = () => {
    selectedProviders.value = [];
  };

  const onResetAll = () => {
    onResetFeatures();
    onResetProviders();
    parseSort();
  };

  const parseFeaturesQuery = () => {
    const { features = [] } = route.query;
    selectedFeatures.value = normalizeToArray(features);
  };

  const parseProvidersQuery = () => {
    const { providers = [] } = route.query;
    selectedProviders.value = normalizeToArray(providers);
  };

  const parseSort = () => {
    activeOption.value = selectedOption(props.sortBy, props.sortOrder);
  };

  const parseQuery = () => {
    parseFeaturesQuery();
    parseProvidersQuery();
    parseSort();
  };

  const toggleFilter = (methodName: 'open' | 'close') => {
    bottomSheet.value[methodName]();
  };

  const onClose = () => {
    parseQuery();
    toggleFilter('close');
  };

  const onApplyFilter = () => {
    updateQueryWithReplace();
    toggleFilter('close');
  };

  const onSelectOption = (value: string | number) => {
    const matched = props.sortOptions.find(option => option.value === value);
    activeOption.value = matched ?? props.sortOptions[0];
  };

  const updateQueryWithReplace = () => {
    router.replace({
      query: {
        ...route.query,
        providers: selectedProviders.value,
        features: selectedFeatures.value,
        sortBy: activeOption.value.sortBy,
        sortOrder: activeOption.value.sortOrder,
      },
    });
  };

  watchEffect(() => {
    parseQuery();
  });
</script>

<style src="~/assets/styles/components/bottom-sheet/games-filter-mobile.scss" lang="scss" />
